import { z } from 'zod';
import { DESIGN_SHAPES, type CustomDesign } from '../menu/custom.js';
import { nameSchema } from './common.js';

/**
 * Validation for custom menu designs. Everything a design holds is bounded: positions and sizes
 * stay near the canvas, texts are short, colours are hex or theme tokens, and there is a cap on
 * elements, so a design can never make the player draw something unbounded.
 */

const coord = z.number().finite().min(-4000).max(12000);
const size = z.number().finite().min(1).max(12000);
const color = z
  .string()
  .regex(/^(#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})|theme:(bg|surface|text|primary|accent))$/i, 'Use a hex colour or a theme colour');
const font = z.enum(['heading', 'body', 'script']);
const slot = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('item'), section: z.number().int().min(0).max(50), item: z.number().int().min(0).max(200) }),
  z.object({ kind: z.literal('featured') }),
]);

const base = {
  id: z.string().trim().min(1).max(40),
  name: z.string().trim().max(60).nullable().optional(),
  x: coord,
  y: coord,
  w: size,
  h: size,
  rotation: z.number().finite().min(-360).max(360).default(0),
  opacity: z.number().min(0).max(1).default(1),
  locked: z.boolean().optional(),
};

export const designElementSchema = z.discriminatedUnion('type', [
  z.object({
    ...base,
    type: z.literal('text'),
    text: z.string().max(500),
    textAr: z.string().max(500).nullable(),
    bind: z
      .discriminatedUnion('kind', [
        z.object({ kind: z.literal('title') }),
        z.object({ kind: z.literal('brand') }),
        z.object({ kind: z.literal('tagline') }),
        z.object({ kind: z.literal('section'), section: z.number().int().min(0).max(50) }),
      ])
      .nullable(),
    font,
    size: z.number().min(4).max(600),
    weight: z.number().int().min(100).max(900),
    color,
    align: z.enum(['left', 'center', 'right']),
    italic: z.boolean(),
    uppercase: z.boolean(),
    letterSpacing: z.number().min(-1).max(2),
    lineHeight: z.number().min(0.6).max(3),
  }),
  z.object({
    ...base,
    type: z.literal('image'),
    mediaId: z.string().uuid().nullable(),
    source: slot.nullable(),
    fit: z.enum(['cover', 'contain']),
    radius: z.number().min(0).max(50),
    borderWidth: z.number().min(0).max(100),
    borderColor: color,
    shadow: z.boolean(),
  }),
  z.object({
    ...base,
    type: z.literal('shape'),
    shape: z.enum(DESIGN_SHAPES),
    fill: color,
    stroke: color.nullable(),
    strokeWidth: z.number().min(0).max(100),
    radius: z.number().min(0).max(500),
    text: z.string().max(120).nullable(),
    textColor: color,
    textSize: z.number().min(4).max(400),
    font,
  }),
  z.object({
    ...base,
    type: z.literal('line'),
    color,
    thickness: z.number().min(1).max(100),
    style: z.enum(['solid', 'dashed', 'dotted']),
  }),
  z.object({
    ...base,
    type: z.literal('menu-list'),
    section: z.number().int().min(0).max(50).nullable(),
    showHeading: z.boolean(),
    columns: z.number().int().min(1).max(4),
    font,
    nameSize: z.number().min(6).max(200),
    uppercase: z.boolean(),
    headingColor: color,
    nameColor: color,
    priceColor: color,
    textColor: color,
    showDescriptions: z.boolean(),
    leader: z.enum(['dotted', 'dashed', 'none']),
  }),
  z.object({
    ...base,
    type: z.literal('item-card'),
    source: slot,
    layout: z.enum(['vertical', 'horizontal']),
    showPhoto: z.boolean(),
    photoShape: z.enum(['circle', 'rounded', 'square']),
    showDescription: z.boolean(),
    showPrice: z.boolean(),
    font,
    nameSize: z.number().min(6).max(200),
    nameColor: color,
    priceColor: color,
    textColor: color,
    align: z.enum(['left', 'center']),
  }),
]);

export const customDesignSchema = z.object({
  width: z.number().int().min(320).max(7680),
  height: z.number().int().min(320).max(7680),
  background: z.object({ color, mediaId: z.string().uuid().nullable(), overlay: z.number().min(0).max(1) }),
  elements: z.array(designElementSchema).max(200),
});

/**
 * The document as players accept it: only its outline is checked. The server validated it in
 * full when it was saved, and a player must never refuse a whole manifest because a newer
 * release added an element type it does not know; the renderer skips those and clamps every
 * number it draws.
 */
export const manifestDesignSchema = z.custom<CustomDesign>(
  (value) =>
    typeof value === 'object' &&
    value !== null &&
    typeof (value as CustomDesign).width === 'number' &&
    typeof (value as CustomDesign).height === 'number' &&
    Array.isArray((value as CustomDesign).elements),
  'Not a design',
);

export const menuDesignSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  name: z.string(),
  orientation: z.enum(['landscape', 'portrait']),
  document: customDesignSchema,
  /** Signed URLs for the design's own pictures, for the editor. */
  mediaUrls: z.record(z.string(), z.string().nullable()),
  /** Boards using the design. */
  menuCount: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type MenuDesign = z.infer<typeof menuDesignSchema>;

export const createMenuDesignSchema = z.object({
  name: nameSchema,
  orientation: z.enum(['landscape', 'portrait']).default('landscape'),
  /** Start from a starter layout, or omit for a blank canvas. */
  starter: z.string().max(40).optional(),
  document: customDesignSchema.optional(),
});
export const updateMenuDesignSchema = z.object({
  name: nameSchema.optional(),
  document: customDesignSchema.optional(),
});
